/**
 * PAY-4644: the second ten construction celebrations.
 *
 * Same contract as the first ten (`construction-scene.ts`): each builder lays
 * its props out against the card's resting rectangle, is a pure function of
 * time and the seed, and ends with the card at rest. Four carry the card in
 * (pile driver, stop/slow bat, tape measure, topping out), one hangs it on a
 * nail and straightens it, one buries it in dirt and bores a tunnel to it,
 * and the rest hold it back until their moment.
 */

import {
  type Builder,
  type Chunk,
  CONCRETE,
  DIRT,
  GLASS,
  HIDDEN,
  ORANGE,
  STEEL,
  STEEL_LIGHT,
  TAU,
  TYRE,
  YELLOW,
  YELLOW_DARK,
  circle,
  drawChunks,
  drawCone,
  drawPuffs,
  drawWheel,
  easeBack,
  easeIn,
  easeInOut,
  easeOut,
  fadeInAtRest,
  dotsByColour,
  fillDots,
  lerp,
  makePuffs,
  pick,
  propsAlpha,
  rand,
  roundRect,
  seg,
} from './construction-kit';

// ── 11. Scaffolding ────────────────────────────────────────────────────────
/** A tube or board; it either grows from `a` or slides in from the side. */
interface Tube {
  a: [number, number];
  b: [number, number];
  width: number;
  color: string;
  at: number;
  strike: number;
  vx: number;
  vy: number;
  spin: number;
  kind: 'grow' | 'slide';
}

const scaffolding: Builder = (stage, rng) => {
  // The scaffold frames the card: an open bay where it sits and tubes only
  // around it, so nothing crosses the text. It comes down outward.
  const pad = 18;
  const ground = stage.height;
  const bay = Math.min(90, Math.max(56, stage.left * 0.35));
  const innerLeft = stage.left - pad;
  const innerRight = stage.right + pad;
  const outerLeft = innerLeft - bay;
  const outerRight = innerRight + bay;
  const openTop = stage.top - pad;
  const openBottom = stage.bottom + pad;
  const tube = '#64748B';
  const poles = [outerLeft, innerLeft, innerRight, outerRight];
  const levels: number[] = [];
  for (let y = ground - 40; y > openBottom + 30; y -= 90) levels.push(y);
  levels.push(openBottom, stage.cy, openTop);

  const tubes: Tube[] = [];
  const outward = (x: number) => (x < stage.cx ? -1 : 1);
  // Struck top-down straight after the reveal.
  const strikeAt = (y: number) =>
    2.7 + ((y - openTop) / (ground - openTop)) * 0.4 + rand(rng, 0, 0.08);
  let alternate = 1;
  const add = (
    a: [number, number],
    b: [number, number],
    width: number,
    color: string,
    at: number,
    strike: number,
    kind: Tube['kind'],
  ) => {
    // A full-width piece leaves to alternating sides; the rest go outward.
    const spans = a[0] < stage.cx && b[0] > stage.cx;
    const side = spans ? (alternate = -alternate) : outward((a[0] + b[0]) / 2);
    tubes.push({
      a,
      b,
      width,
      color,
      at,
      strike,
      vx: side * rand(rng, 140, 300),
      vy: rand(rng, -160, -40),
      spin: side * rand(rng, 1, 3),
      kind,
    });
  };
  poles.forEach((x, i) =>
    add(
      [x, ground],
      [x, openTop - 12],
      5,
      tube,
      0.1 + i * 0.12,
      3.05 + rand(rng, 0, 0.1),
      'grow',
    ),
  );
  levels.forEach((y, i) => {
    const at = 0.7 + i * (1 / levels.length);
    // Ledgers run the full width above and below the opening, and only
    // across the end bays beside it.
    if (y > openTop && y <= openBottom) {
      add(
        [outerLeft - 10, y],
        [innerLeft, y],
        4,
        tube,
        at,
        strikeAt(y),
        'slide',
      );
      add(
        [innerRight, y],
        [outerRight + 10, y],
        4,
        tube,
        at,
        strikeAt(y),
        'slide',
      );
    } else {
      add(
        [outerLeft - 10, y],
        [outerRight + 10, y],
        4,
        tube,
        at,
        strikeAt(y),
        'slide',
      );
    }
    if (i > 0) {
      const below = levels[i - 1]!;
      add(
        [outerLeft, below],
        [innerLeft, y],
        3,
        STEEL_LIGHT,
        at + 0.3,
        strikeAt(y),
        'grow',
      );
      add(
        [outerRight, below],
        [innerRight, y],
        3,
        STEEL_LIGHT,
        at + 0.3,
        strikeAt(y),
        'grow',
      );
    }
    if (y === openBottom || y === openTop) {
      add(
        [outerLeft - 6, y - 5],
        [outerRight + 6, y - 5],
        7,
        '#A16207',
        at + 0.4,
        strikeAt(y) + 0.05,
        'slide',
      );
    }
  });
  const reveal = { from: 2.1, to: 2.7 };

  return {
    settledAt: reveal.to,
    place: (t) => fadeInAtRest(t, reveal.from, reveal.to),
    draw(ctx, t) {
      ctx.lineCap = 'round';
      for (const piece of tubes) {
        if (t < piece.at) continue;
        let [ax, ay] = piece.a;
        let [bx, by] = piece.b;
        const p = easeOut(seg(t, piece.at, piece.at + 0.45));
        if (piece.kind === 'grow') {
          bx = lerp(ax, bx, p);
          by = lerp(ay, by, p);
        } else {
          const off = (1 - p) * outward((ax + bx) / 2) * stage.width;
          ax += off;
          bx += off;
        }
        let alpha = 1;
        if (t > piece.strike && piece.kind === 'slide') {
          // Ledgers and boards slide out along their own level, never
          // across the card.
          const off =
            easeIn(seg(t, piece.strike, piece.strike + 0.5)) *
            Math.sign(piece.vx) *
            stage.width *
            1.2;
          ax += off;
          bx += off;
        } else if (t > piece.strike) {
          const k = t - piece.strike;
          const mx = (ax + bx) / 2 + piece.vx * k;
          const my = (ay + by) / 2 + piece.vy * k + 900 * k * k;
          const hx = (bx - ax) / 2;
          const hy = (by - ay) / 2;
          const cos = Math.cos(piece.spin * k);
          const sin = Math.sin(piece.spin * k);
          ax = mx - (hx * cos - hy * sin);
          ay = my - (hx * sin + hy * cos);
          bx = mx + (hx * cos - hy * sin);
          by = my + (hx * sin + hy * cos);
          alpha = 1 - seg(k, 0.4, 0.75);
        }
        if (alpha <= 0) continue;
        ctx.globalAlpha = alpha;
        ctx.strokeStyle = piece.color;
        ctx.lineWidth = piece.width;
        ctx.beginPath();
        ctx.moveTo(ax, ay);
        ctx.lineTo(bx, by);
        ctx.stroke();
      }
      // Couplers at the joints while it stands.
      const standing = seg(t, 1.3, 1.7) * (1 - seg(t, 2.6, 2.8));
      if (standing > 0) {
        ctx.globalAlpha = standing;
        ctx.fillStyle = ORANGE;
        for (const x of poles) {
          for (const y of levels) {
            circle(ctx, x, y, 3.5);
            ctx.fill();
          }
        }
      }
      ctx.globalAlpha = 1;
      ctx.lineCap = 'butt';
    },
  };
};

// ── 12. Pile driver ────────────────────────────────────────────────────────
const STRIKES = [1.5, 2.25, 3.0];

const pileDriver: Builder = (stage, rng) => {
  const depth = Math.min(180, stage.top + stage.cardHeight * 0.4);
  const enter = { from: 0.2, to: 1.0 };
  const startY = stage.cy - depth;
  const driven = (t: number) =>
    STRIKES.reduce((sum, s) => sum + easeOut(seg(t, s, s + 0.08)), 0);
  const recoil = (t: number) =>
    STRIKES.reduce(
      (sum, s) =>
        t > s
          ? sum + 5 * Math.sin(40 * (t - s)) * Math.exp(-14 * (t - s))
          : sum,
      0,
    );
  const cardY = (t: number) =>
    t < enter.to
      ? lerp(-stage.cardHeight, startY, easeOut(seg(t, enter.from, enter.to)))
      : startY + (depth * driven(t)) / STRIKES.length + recoil(t);
  const raised = 130;
  // The hammer's gap above the card: lifted, dropped onto each strike, then
  // lifted clear.
  const gap = (t: number) => {
    for (let i = 0; i < STRIKES.length; i += 1) {
      const s = STRIKES[i]!;
      if (t < s - 0.15) {
        return i === 0
          ? raised
          : raised * easeOut(seg(t, STRIKES[i - 1]! + 0.1, s - 0.2));
      }
      if (t < s) return raised * (1 - easeIn(seg(t, s - 0.15, s)));
      if (t < s + 0.1) return 0;
    }
    return raised * easeOut(seg(t, 3.1, 3.6));
  };
  const rigLift = (t: number) =>
    easeIn(seg(t, 4.4, 6.0)) * (stage.height + 200);
  const puffs = STRIKES.flatMap((s) => [
    ...makePuffs(rng, 8, stage.left - 10, stage.left + 40, 0, s),
    ...makePuffs(rng, 8, stage.right - 40, stage.right + 10, 0, s),
  ]);
  const hammerWidth = Math.min(120, stage.cardWidth * 0.4);
  const settledAt = 3.4;

  return {
    settledAt,
    place: (t) => (t < enter.from ? HIDDEN : { y: cardY(t) }),
    draw(ctx, t) {
      const lift = rigLift(t);
      const cardTop = cardY(Math.min(t, settledAt)) - stage.cardHeight / 2;
      const hammerBottom = cardTop - gap(t) - lift;
      const rail = hammerWidth / 2 + 10;
      ctx.globalAlpha = propsAlpha(t);
      // Leader rails down from the top of the screen.
      ctx.strokeStyle = STEEL;
      ctx.lineWidth = 4;
      ctx.beginPath();
      for (const side of [-1, 1]) {
        ctx.moveTo(stage.cx + side * rail, -10 - lift);
        ctx.lineTo(stage.cx + side * rail, hammerBottom - 6);
      }
      ctx.stroke();
      ctx.strokeStyle = YELLOW_DARK;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      for (let y = hammerBottom - 6, i = 0; y > -10 - lift; y -= 28, i += 1) {
        ctx.moveTo(stage.cx + (i % 2 ? rail : -rail), y);
        ctx.lineTo(stage.cx + (i % 2 ? -rail : rail), y - 28);
      }
      ctx.stroke();
      // Cable and hammer.
      ctx.strokeStyle = STEEL_LIGHT;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(stage.cx, -10);
      ctx.lineTo(stage.cx, hammerBottom - 70);
      ctx.stroke();
      const left = stage.cx - hammerWidth / 2;
      ctx.fillStyle = STEEL;
      roundRect(ctx, left, hammerBottom - 70, hammerWidth, 64, 6);
      ctx.fill();
      ctx.save();
      ctx.beginPath();
      ctx.rect(left, hammerBottom - 60, hammerWidth, 14);
      ctx.clip();
      ctx.fillStyle = YELLOW;
      for (let x = left - 6; x < left + hammerWidth; x += 34) {
        ctx.beginPath();
        ctx.moveTo(x, hammerBottom - 46);
        ctx.lineTo(x + 14, hammerBottom - 60);
        ctx.lineTo(x + 28, hammerBottom - 60);
        ctx.lineTo(x + 14, hammerBottom - 46);
        ctx.fill();
      }
      ctx.restore();
      ctx.fillStyle = '#334155';
      ctx.fillRect(left - 4, hammerBottom - 10, hammerWidth + 8, 10);
      ctx.globalAlpha = 1;
      // Dust off the card's top corners and impact lines on each strike.
      ctx.save();
      ctx.translate(0, cardTop);
      drawPuffs(ctx, puffs, t);
      ctx.restore();
      ctx.strokeStyle = YELLOW;
      ctx.lineWidth = 3;
      for (const s of STRIKES) {
        const k = seg(t, s, s + 0.25);
        if (k <= 0 || k >= 1) continue;
        ctx.globalAlpha = 1 - k;
        ctx.beginPath();
        for (const side of [-1, 1]) {
          for (let i = 0; i < 3; i += 1) {
            const x = stage.cx + side * (hammerWidth / 2 + 14);
            const y = cardTop - 8 - i * 14;
            ctx.moveTo(x + side * 6 * k, y);
            ctx.lineTo(x + side * (6 + 26 * k), y - 6 + i * 3);
          }
        }
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    },
  };
};

// ── 13. Road roller ────────────────────────────────────────────────────────
const roadRoller: Builder = (stage, rng) => {
  const ground = stage.height - 26;
  const run = { from: 0.2, to: 4.8 };
  const startX = -260;
  const endX = stage.width + 300;
  const rollerX = (t: number) => lerp(startX, endX, seg(t, run.from, run.to));
  // The card springs out of the road once the drum (radius 38, 70px ahead of
  // the roller's origin) has rolled clear of its right edge.
  const passes =
    run.from +
    ((stage.right + 38 - 70 - startX) / (endX - startX)) * (run.to - run.from);
  const pop = { from: passes, to: passes + 0.8 };
  const gravel = dotsByColour(
    Array.from({ length: Math.round(stage.width / 6) }, () => ({
      x: rand(rng, 0, stage.width),
      y: ground + rand(rng, -4, 14),
      r: rand(rng, 1.2, 3),
      color: pick(rng, CONCRETE),
    })),
  );
  const steam = Array.from({ length: 14 }, (_, i) => ({
    at: run.from + i * 0.32,
    drift: rand(rng, -8, 8),
  }));

  return {
    settledAt: pop.to,
    place(t) {
      if (t < pop.from) return HIDDEN;
      const p = seg(t, pop.from, pop.to);
      const scale = lerp(0.2, 1, easeBack(p));
      return {
        y: lerp(ground - (stage.cardHeight * 0.2) / 2, stage.cy, easeBack(p)),
        scale,
        opacity: Math.min(1, p * 5),
      };
    },
    draw(ctx, t) {
      const x = rollerX(t);
      const drumX = x + 70;
      ctx.globalAlpha = propsAlpha(t);
      // Gravel ahead of the drum, fresh asphalt and lane lines behind it.
      fillDots(ctx, gravel, (stone) => stone.x < drumX - 20);
      const laid = Math.max(0, Math.min(stage.width, drumX));
      ctx.fillStyle = stage.dark ? '#1F2937' : '#374151';
      ctx.fillRect(0, ground - 4, laid, stage.height - ground + 4);
      ctx.fillStyle = '#F8FAFC';
      for (let lane = 10; lane < laid - 30; lane += 60) {
        ctx.fillRect(lane, ground + 10, 32, 4);
      }
      // Rear wheel, body, canopy and seat.
      drawWheel(ctx, x - 70, ground - 26, 26, -x / 26);
      ctx.fillStyle = YELLOW;
      roundRect(ctx, x - 110, ground - 80, 150, 46, 8);
      ctx.fill();
      ctx.fillStyle = YELLOW_DARK;
      ctx.fillRect(x - 110, ground - 44, 150, 6);
      ctx.strokeStyle = STEEL;
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(x - 90, ground - 80);
      ctx.lineTo(x - 90, ground - 140);
      ctx.lineTo(x - 20, ground - 140);
      ctx.lineTo(x - 20, ground - 80);
      ctx.stroke();
      ctx.fillStyle = STEEL;
      ctx.fillRect(x - 96, ground - 146, 82, 8);
      ctx.fillRect(x - 100, ground - 100, 6, 22);
      ctx.fillStyle = TYRE;
      roundRect(ctx, x - 70, ground - 106, 24, 22, 4);
      ctx.fill();
      // Drum frame and drum.
      ctx.fillStyle = YELLOW;
      roundRect(ctx, x + 24, ground - 84, 92, 18, 5);
      ctx.fill();
      ctx.fillStyle = STEEL_LIGHT;
      circle(ctx, drumX, ground - 36, 38);
      ctx.fill();
      ctx.strokeStyle = STEEL;
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      for (let i = 0; i < 6; i += 1) {
        const angle = -x / 38 + (i * TAU) / 6;
        ctx.moveTo(
          drumX + Math.cos(angle) * 12,
          ground - 36 + Math.sin(angle) * 12,
        );
        ctx.lineTo(
          drumX + Math.cos(angle) * 34,
          ground - 36 + Math.sin(angle) * 34,
        );
      }
      ctx.stroke();
      ctx.fillStyle = STEEL;
      circle(ctx, drumX, ground - 36, 9);
      ctx.fill();
      // Exhaust.
      const fade = propsAlpha(t);
      ctx.fillStyle = STEEL_LIGHT;
      for (const puff of steam) {
        const k = (t - puff.at) / 1.1;
        if (k < 0 || k > 1) continue;
        ctx.globalAlpha = fade * (1 - k) * 0.5;
        circle(
          ctx,
          rollerX(puff.at) - 97 + puff.drift * k - 30 * k,
          ground - 104 - 70 * k,
          6 + 12 * k,
        );
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    },
  };
};

// ── 14. Stop/slow bat ──────────────────────────────────────────────────────
const stopSlow: Builder = (stage) => {
  const ground = stage.height - 16;
  const waitScale = 0.42;
  const waitX = stage.width - 28 - (stage.cardWidth * waitScale) / 2;
  const waitY = ground - (stage.cardHeight * waitScale) / 2;
  const signX = Math.min(stage.width - 70, stage.right + 80);
  const signY = Math.min(ground - 190, stage.cy);
  const flip = { from: 2.0, to: 2.5 };
  const drive = { from: 2.6, to: 4.2 };
  const cones = Array.from({ length: 6 }, (_, i) => ({
    x: lerp(stage.cx, stage.width - 30, i / 5),
    at: 0.2 + i * 0.1,
  }));

  return {
    settledAt: drive.to,
    place(t) {
      if (t < drive.from) {
        // Waiting in the corner, engine idling.
        return {
          x: waitX,
          y: waitY + Math.sin(t * 30) * 0.8,
          scale: waitScale,
          opacity: seg(t, 0.3, 0.7),
        };
      }
      const p = easeInOut(seg(t, drive.from, drive.to));
      return {
        x: lerp(waitX, stage.cx, p),
        y: lerp(waitY, stage.cy, p),
        scale: lerp(waitScale, 1, p),
        rotation: 0.03 * Math.sin(p * Math.PI),
      };
    },
    draw(ctx, t) {
      ctx.globalAlpha = propsAlpha(t);
      for (const cone of cones) {
        drawCone(
          ctx,
          cone.x,
          ground + 12,
          34,
          easeBack(seg(t, cone.at, cone.at + 0.3)),
        );
      }
      // The traffic controller: legs, hi-vis vest, head and hard hat.
      const px = signX + 34;
      ctx.lineCap = 'round';
      ctx.strokeStyle = '#1E3A8A';
      ctx.lineWidth = 8;
      ctx.beginPath();
      ctx.moveTo(px - 7, ground);
      ctx.lineTo(px - 5, ground - 56);
      ctx.moveTo(px + 7, ground);
      ctx.lineTo(px + 5, ground - 56);
      ctx.stroke();
      ctx.fillStyle = ORANGE;
      roundRect(ctx, px - 16, ground - 116, 32, 64, 8);
      ctx.fill();
      ctx.fillStyle = '#E5E7EB';
      ctx.fillRect(px - 16, ground - 92, 32, 5);
      ctx.fillRect(px - 16, ground - 76, 32, 5);
      ctx.fillStyle = '#FCD9B6';
      circle(ctx, px, ground - 130, 12);
      ctx.fill();
      ctx.fillStyle = YELLOW;
      ctx.beginPath();
      ctx.arc(px, ground - 134, 14, Math.PI, 0);
      ctx.fill();
      ctx.fillRect(px - 18, ground - 136, 36, 4);
      ctx.strokeStyle = ORANGE;
      ctx.lineWidth = 7;
      ctx.beginPath();
      ctx.moveTo(px - 10, ground - 104);
      ctx.lineTo(signX + 4, ground - 90);
      ctx.stroke();
      ctx.lineCap = 'butt';
      // Pole and bat, turned edge-on to flip from STOP to SLOW.
      ctx.strokeStyle = STEEL_LIGHT;
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.moveTo(signX, ground);
      ctx.lineTo(signX, signY + 44);
      ctx.stroke();
      const turn = seg(t, flip.from, flip.to);
      const slow = turn >= 0.5;
      ctx.save();
      ctx.translate(signX, signY);
      ctx.scale(Math.max(0.02, Math.abs(Math.cos(turn * Math.PI))), 1);
      ctx.fillStyle = slow ? '#FACC15' : '#DC2626';
      circle(ctx, 0, 0, 46);
      ctx.fill();
      ctx.strokeStyle = '#F8FAFC';
      ctx.lineWidth = 4;
      circle(ctx, 0, 0, 41);
      ctx.stroke();
      ctx.fillStyle = slow ? '#111827' : '#F8FAFC';
      ctx.font = 'bold 22px ui-sans-serif, system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(slow ? 'SLOW' : 'STOP', 0, 1);
      ctx.restore();
      ctx.globalAlpha = 1;
    },
  };
};

// ── 15. Surveyor ───────────────────────────────────────────────────────────
const surveyor: Builder = (stage) => {
  const ground = stage.height - 12;
  // Left of the card where there is room; never higher than just below the
  // card, so the instrument (drawn over the card) never covers its text.
  const baseX = Math.max(70, Math.min(stage.left - 120, stage.width * 0.12));
  const head = { x: baseX, y: Math.max(ground - 150, stage.bottom + 46) };
  const corners = [
    { x: stage.left, y: stage.top },
    { x: stage.right, y: stage.top },
    { x: stage.right, y: stage.bottom },
    { x: stage.left, y: stage.bottom },
  ];
  const pegAt = (i: number) => 0.7 + i * 0.4;
  // The laser dot travels corner to corner, pegging each as it arrives.
  const dot = (t: number) => {
    const i = Math.min(3, Math.max(0, Math.floor((t - 0.5) / 0.4)));
    const p = easeInOut(seg(t, 0.5 + i * 0.4, 0.7 + i * 0.4));
    const to = corners[i]!;
    const from =
      i === 0 ? { x: head.x + 200, y: head.y - 40 } : corners[i - 1]!;
    return { x: lerp(from.x, to.x, p), y: lerp(from.y, to.y, p) };
  };
  const reveal = { from: 2.5, to: 3.1 };
  const laser = stage.dark ? '#F87171' : '#DC2626';
  const label = stage.dark ? '#CBD5E1' : '#334155';

  return {
    settledAt: reveal.to,
    place: (t) => fadeInAtRest(t, reveal.from, reveal.to),
    draw(ctx, t) {
      const fade = propsAlpha(t);
      const marks = fade * (1 - seg(t, 4.6, 5.6));
      ctx.globalAlpha = fade;
      // Tripod and theodolite.
      ctx.strokeStyle = YELLOW_DARK;
      ctx.lineWidth = 4;
      ctx.beginPath();
      for (const leg of [-40, 0, 40]) {
        ctx.moveTo(head.x, head.y + 16);
        ctx.lineTo(head.x + leg, ground);
      }
      ctx.stroke();
      ctx.fillStyle = STEEL;
      roundRect(ctx, head.x - 18, head.y + 6, 36, 10, 3);
      ctx.fill();
      ctx.fillStyle = YELLOW;
      roundRect(ctx, head.x - 14, head.y - 30, 28, 36, 5);
      ctx.fill();
      ctx.fillStyle = STEEL;
      roundRect(ctx, head.x - 6, head.y - 22, 30, 12, 4);
      ctx.fill();
      ctx.fillStyle = GLASS;
      circle(ctx, head.x + 24, head.y - 16, 5);
      ctx.fill();
      if (t > 0.5 && t < 2.3) {
        const at = dot(t);
        ctx.strokeStyle = laser;
        ctx.fillStyle = laser;
        ctx.lineWidth = 1.5;
        ctx.shadowColor = laser;
        ctx.shadowBlur = 10;
        ctx.beginPath();
        ctx.moveTo(head.x + 28, head.y - 16);
        ctx.lineTo(at.x, at.y);
        ctx.stroke();
        circle(ctx, at.x, at.y, 4);
        ctx.fill();
        ctx.shadowBlur = 0;
      }
      // Corner pegs.
      ctx.strokeStyle = laser;
      ctx.lineWidth = 2;
      corners.forEach((corner, i) => {
        const k = seg(t, pegAt(i), pegAt(i) + 0.15);
        if (k <= 0) return;
        const arm = 10 * easeBack(k);
        ctx.globalAlpha = marks * k;
        ctx.beginPath();
        ctx.moveTo(corner.x - arm, corner.y);
        ctx.lineTo(corner.x + arm, corner.y);
        ctx.moveTo(corner.x, corner.y - arm);
        ctx.lineTo(corner.x, corner.y + arm);
        ctx.stroke();
        circle(ctx, corner.x, corner.y, 14 * (1 - k) + 2);
        ctx.stroke();
      });
      // String line around the pegs.
      const string = seg(t, 2.1, 2.6);
      if (string > 0) {
        ctx.globalAlpha = marks;
        ctx.lineWidth = 1;
        ctx.setLineDash([6, 4]);
        ctx.lineDashOffset = -t * 20;
        let left = 2 * (stage.cardWidth + stage.cardHeight) * string;
        ctx.beginPath();
        ctx.moveTo(corners[0]!.x, corners[0]!.y);
        for (let i = 1; i <= 4 && left > 0; i += 1) {
          const from = corners[i - 1]!;
          const to = corners[i % 4]!;
          const length = Math.hypot(to.x - from.x, to.y - from.y);
          const p = Math.min(1, left / length);
          ctx.lineTo(lerp(from.x, to.x, p), lerp(from.y, to.y, p));
          left -= length;
        }
        ctx.stroke();
        ctx.setLineDash([]);
      }
      // Dimensions.
      const dimensions = seg(t, 2.3, 2.8);
      if (dimensions > 0) {
        const y = stage.top - 22;
        const x = stage.right + 22;
        ctx.globalAlpha = marks * dimensions;
        ctx.strokeStyle = label;
        ctx.fillStyle = label;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(stage.left, y);
        ctx.lineTo(stage.right, y);
        ctx.moveTo(x, stage.top);
        ctx.lineTo(x, stage.bottom);
        ctx.stroke();
        const arrows = [
          [stage.left, y, 0],
          [stage.right, y, Math.PI],
          [x, stage.top, Math.PI / 2],
          [x, stage.bottom, -Math.PI / 2],
        ] as const;
        for (const [ax, ay, dir] of arrows) {
          ctx.beginPath();
          ctx.moveTo(ax, ay);
          ctx.lineTo(
            ax + Math.cos(dir - 0.4) * 8,
            ay + Math.sin(dir - 0.4) * 8,
          );
          ctx.lineTo(
            ax + Math.cos(dir + 0.4) * 8,
            ay + Math.sin(dir + 0.4) * 8,
          );
          ctx.fill();
        }
        ctx.font = '12px ui-monospace, SFMono-Regular, Menlo, monospace';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(`${Math.round(stage.cardWidth)} mm`, stage.cx, y - 4);
        ctx.save();
        ctx.translate(x + 6, stage.cy);
        ctx.rotate(Math.PI / 2);
        ctx.fillText(`${Math.round(stage.cardHeight)} mm`, 0, 0);
        ctx.restore();
      }
      ctx.globalAlpha = 1;
    },
  };
};

// ── 16. Tunnel borer ───────────────────────────────────────────────────────
const BORE = { from: 0.2, to: 3.4 };
const DIRT_CLEARS = { from: 4.6, to: 5.6 };

const tunnelBorer: Builder = (stage, rng) => {
  // The whole screen is dirt. The machine bores left to right at the card's
  // level and carries on out of the right edge; the tunnel it leaves is a gap
  // just taller than the card, so the card shows through it. Then the dirt
  // clears away.
  const half = stage.cardHeight / 2 + 24;
  const gapTop = stage.cy - half;
  const gapBottom = stage.cy + half;
  const radius = half - 4;
  const body = Math.max(radius * 2.6, 180);
  const startX = -radius;
  const endX = stage.width + body + radius;
  const headX = (t: number) =>
    lerp(startX, endX, easeInOut(seg(t, BORE.from, BORE.to)));
  // Released once the machine's tail has cleared the card.
  let passed = BORE.to;
  for (let lo = BORE.from, hi = BORE.to, i = 0; i < 30; i += 1) {
    passed = (lo + hi) / 2;
    if (headX(passed) - body > stage.right) hi = passed;
    else lo = passed;
  }
  const strata = Array.from({ length: 7 }, (_, i) => ({
    y: (stage.height * i) / 7 + rand(rng, -12, 12),
    color: pick(rng, DIRT),
  }));
  // Capped, and filled one colour at a time, so a 4K screen stays cheap.
  const stones = dotsByColour(
    Array.from(
      {
        length: Math.min(900, Math.round((stage.width * stage.height) / 2600)),
      },
      () => ({
        x: rand(rng, 0, stage.width),
        y: rand(rng, 0, stage.height),
        r: rand(rng, 1.5, 5),
        color: pick(rng, CONCRETE),
      }),
    ),
  );
  // Spoil crumbling off the cutter face as it goes.
  const spoil: Chunk[] = Array.from({ length: 60 }, (_, i) => {
    const te = lerp(BORE.from + 0.1, BORE.to - 0.4, i / 59);
    return {
      x: headX(te) + rand(rng, 0, 10),
      y: stage.cy + rand(rng, -radius * 0.8, radius * 0.8),
      vx: rand(rng, -160, -40),
      vy: rand(rng, -120, 20),
      g: 900,
      te,
      life: 0.6,
      size: rand(rng, 5, 11),
      spin: rand(rng, -6, 6),
      color: pick(rng, DIRT),
    };
  });

  return {
    settledAt: passed,
    // At rest throughout, buried until the tunnel reaches it. Hidden only
    // for the first frame, before the dirt has been drawn over it.
    place: (t) => (t < 0.05 ? HIDDEN : {}),
    draw(ctx, t) {
      const x = headX(t);
      const tail = Math.max(0, Math.min(stage.width, x - body));
      const dirt = 1 - seg(t, DIRT_CLEARS.from, DIRT_CLEARS.to);
      if (dirt > 0) {
        // Everything but the open tunnel behind the machine.
        ctx.save();
        ctx.beginPath();
        ctx.rect(0, 0, stage.width, stage.height);
        if (tail > 0) ctx.rect(0, gapTop, tail, gapBottom - gapTop);
        ctx.clip('evenodd');
        ctx.globalAlpha = dirt;
        ctx.fillStyle = stage.dark ? '#3B2A1A' : '#92400E';
        ctx.fillRect(0, 0, stage.width, stage.height);
        for (const band of strata) {
          ctx.fillStyle = band.color;
          ctx.globalAlpha = dirt * 0.35;
          ctx.fillRect(0, band.y, stage.width, stage.height / 14);
        }
        ctx.globalAlpha = dirt * 0.8;
        fillDots(ctx, stones);
        ctx.restore();
        // Lining along the tunnel's roof and floor, never across the card.
        if (tail > 0) {
          ctx.globalAlpha = dirt;
          ctx.fillStyle = CONCRETE[0]!;
          ctx.fillRect(0, gapTop - 8, tail, 8);
          ctx.fillRect(0, gapBottom, tail, 8);
          ctx.fillStyle = STEEL;
          for (let ring = 20; ring < tail; ring += 48) {
            ctx.fillRect(ring, gapTop - 8, 2, 8);
            ctx.fillRect(ring, gapBottom, 2, 8);
          }
        }
      }
      ctx.globalAlpha = 1;
      if (x - body > stage.width) return;
      drawChunks(ctx, spoil, t);
      // Shield, trailing back-up gear and the turning cutter head.
      ctx.fillStyle = YELLOW;
      ctx.fillRect(x - body, stage.cy - radius * 0.85, body - 10, radius * 1.7);
      ctx.fillStyle = YELLOW_DARK;
      ctx.fillRect(x - body, stage.cy - radius * 0.85, body - 10, 8);
      ctx.fillStyle = STEEL;
      for (let i = 1; i < 4; i += 1) {
        ctx.fillRect(
          x - body + (i * body) / 4,
          stage.cy - radius * 0.85,
          4,
          radius * 1.7,
        );
      }
      ctx.save();
      ctx.translate(x, stage.cy);
      ctx.scale(0.35, 1);
      ctx.fillStyle = STEEL;
      circle(ctx, 0, 0, radius);
      ctx.fill();
      ctx.strokeStyle = STEEL_LIGHT;
      ctx.lineWidth = 6;
      ctx.beginPath();
      for (let i = 0; i < 6; i += 1) {
        const angle = t * 9 + (i * TAU) / 6;
        ctx.moveTo(0, 0);
        ctx.lineTo(Math.cos(angle) * radius, Math.sin(angle) * radius);
      }
      ctx.stroke();
      ctx.fillStyle = YELLOW;
      circle(ctx, 0, 0, radius * 0.18);
      ctx.fill();
      ctx.restore();
    },
  };
};

// ── 17. Demolition ─────────────────────────────────────────────────────────
const PLUNGE_AT = 2.0;

const demolition: Builder = (stage, rng) => {
  const ground = stage.height;
  const towerWidth = Math.max(140, stage.cardWidth * 0.62);
  const towerTop = Math.max(20, stage.top - 60);
  const collapse = { from: 2.25, to: 3.3 };
  const box = { x: Math.max(30, stage.left - 190), y: ground - 50 };
  const charges = Array.from({ length: 6 }, (_, i) => ({
    x: stage.cx + lerp(-towerWidth / 2, towerWidth / 2, i / 5),
    y: ground - rand(rng, 20, 120),
    at: PLUNGE_AT + 0.05 + i * 0.04,
  }));
  const dust = [
    ...makePuffs(
      rng,
      50,
      stage.cx - towerWidth,
      stage.cx + towerWidth,
      ground - 30,
      collapse.from,
      3,
    ),
    ...makePuffs(
      rng,
      40,
      stage.left - 20,
      stage.right + 20,
      stage.cy + 40,
      collapse.from + 0.3,
      2.2,
    ),
    ...makePuffs(
      rng,
      30,
      stage.left,
      stage.right,
      stage.top + 20,
      collapse.from + 0.6,
      1.6,
    ),
  ].map((puff) => ({ ...puff, size: puff.size * 4, life: puff.life * 2.8 }));
  const reveal = { from: 2.9, to: 3.6 };

  return {
    settledAt: reveal.to,
    place: (t) => fadeInAtRest(t, reveal.from, reveal.to),
    draw(ctx, t) {
      const fade = propsAlpha(t);
      ctx.globalAlpha = fade;
      // The tower sinks into its own dust, leaning a little.
      const fall = easeIn(seg(t, collapse.from, collapse.to));
      const top = towerTop + fall * (ground - towerTop);
      if (top < ground) {
        const left = stage.cx - towerWidth / 2;
        ctx.save();
        ctx.beginPath();
        ctx.rect(0, 0, stage.width, ground);
        ctx.clip();
        ctx.translate(stage.cx, ground);
        ctx.rotate(0.06 * fall);
        ctx.translate(-stage.cx, -ground);
        ctx.fillStyle = stage.dark ? '#6B7280' : '#9CA3AF';
        ctx.fillRect(left, top, towerWidth, ground - top);
        ctx.fillStyle = stage.dark ? '#374151' : '#E5E7EB';
        for (let y = top + 16; y < ground - 20; y += 34) {
          for (let x = left + 14; x < left + towerWidth - 24; x += 30) {
            ctx.fillRect(x, y, 16, 20);
          }
        }
        ctx.fillStyle = STEEL;
        ctx.fillRect(left - 6, top - 8, towerWidth + 12, 10);
        ctx.restore();
      }
      // Detonator, firing line and countdown.
      ctx.strokeStyle = '#DC2626';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(box.x + 30, box.y + 10);
      ctx.quadraticCurveTo(
        (box.x + stage.cx) / 2,
        ground - 4,
        stage.cx - towerWidth / 2,
        ground - 20,
      );
      ctx.stroke();
      ctx.fillStyle = '#7F1D1D';
      roundRect(ctx, box.x, box.y, 60, 40, 5);
      ctx.fill();
      ctx.fillStyle = YELLOW;
      ctx.fillRect(box.x, box.y + 14, 60, 6);
      const handle =
        box.y - 34 + easeIn(seg(t, PLUNGE_AT - 0.15, PLUNGE_AT)) * 26;
      ctx.strokeStyle = STEEL;
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.moveTo(box.x + 30, box.y);
      ctx.lineTo(box.x + 30, handle);
      ctx.moveTo(box.x + 10, handle);
      ctx.lineTo(box.x + 50, handle);
      ctx.stroke();
      if (t >= 0.4 && t < 1.9) {
        const k = ((t - 0.4) % 0.5) / 0.5;
        ctx.globalAlpha = fade * (1 - k);
        ctx.fillStyle = '#DC2626';
        ctx.font = `bold ${Math.round(48 + 30 * k)}px ui-sans-serif, system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(
          String(3 - Math.floor((t - 0.4) / 0.5)),
          box.x + 30,
          box.y - 80,
        );
      }
      for (const charge of charges) {
        const k = seg(t, charge.at, charge.at + 0.35);
        if (k <= 0 || k >= 1) continue;
        ctx.globalAlpha = fade * (1 - k);
        ctx.fillStyle = k < 0.3 ? '#FEF08A' : ORANGE;
        circle(ctx, charge.x, charge.y, 8 + 34 * easeOut(k));
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      drawPuffs(ctx, dust, t);
    },
  };
};

// ── 18. Tape measure ───────────────────────────────────────────────────────
const tapeMeasure: Builder = (stage) => {
  const caseX = Math.max(16, Math.min(60, stage.left - 120));
  const blade = stage.bottom;
  const extend = { from: 0.3, to: 2.4 };
  const snap = { from: 3.0, to: 3.35 };
  const start = caseX + 70;
  // The card rides the hook, its right edge on it and its bottom on the blade.
  const tipX = (t: number) =>
    t < snap.from
      ? lerp(start, stage.right, easeBack(seg(t, extend.from, extend.to)))
      : lerp(stage.right, start, easeIn(seg(t, snap.from, snap.to)));
  const caseShift = (t: number) => -easeIn(seg(t, 5.0, 6.0)) * (caseX + 160);
  const settledAt = extend.to + 0.05;

  return {
    settledAt,
    place(t) {
      if (t < extend.from) return HIDDEN;
      const scale = lerp(0.3, 1, easeOut(seg(t, extend.from, extend.to)));
      return {
        x: tipX(t) - (stage.cardWidth * scale) / 2,
        y: blade - (stage.cardHeight * scale) / 2,
        scale,
        opacity: seg(t, extend.from, extend.from + 0.25),
      };
    },
    draw(ctx, t) {
      const shift = caseShift(t);
      const tip = tipX(t);
      ctx.globalAlpha = propsAlpha(t);
      if (tip > start + 1) {
        ctx.fillStyle = '#FDE047';
        ctx.fillRect(start + shift, blade, tip - start, 20);
        ctx.strokeStyle = '#111827';
        ctx.fillStyle = '#111827';
        ctx.lineWidth = 1;
        ctx.font = '9px ui-monospace, Menlo, monospace';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.beginPath();
        for (let i = 0; start + i * 10 < tip - 2; i += 1) {
          const x = start + shift + i * 10;
          ctx.moveTo(x, blade);
          ctx.lineTo(x, blade + (i % 10 === 0 ? 10 : i % 5 === 0 ? 7 : 4));
          if (i % 10 === 0 && i > 0) ctx.fillText(String(i), x, blade + 10);
        }
        ctx.stroke();
        ctx.fillStyle = STEEL;
        ctx.fillRect(tip - 3, blade - 10, 5, 32);
      }
      const x = caseX + shift;
      ctx.fillStyle = YELLOW;
      roundRect(ctx, x, blade - 40, 74, 74, 16);
      ctx.fill();
      ctx.fillStyle = TYRE;
      circle(ctx, x + 37, blade - 3, 22);
      ctx.fill();
      ctx.fillStyle = STEEL_LIGHT;
      circle(ctx, x + 37, blade - 3, 8);
      ctx.fill();
      ctx.fillStyle = '#DC2626';
      roundRect(ctx, x + 22, blade - 52, 30, 12, 4);
      ctx.fill();
      ctx.globalAlpha = 1;
    },
  };
};

// ── 19. Hammer and nails ───────────────────────────────────────────────────
const NAIL_AT = [2.4, 2.85, 3.3];

const hammerNails: Builder = (stage) => {
  const inset = 14;
  const nails = [
    { x: stage.left + inset, y: stage.top + inset },
    { x: stage.right - inset, y: stage.top + inset },
    { x: stage.right - inset, y: stage.bottom - inset },
    { x: stage.left + inset, y: stage.bottom - inset },
  ];
  const pivot = nails[0]!;
  const enter = { from: 0.2, to: 0.8 };
  const level = { from: 1.6, to: 2.3 };
  // Hanging from the first nail, the card's centre sits straight below it.
  const hang = Math.atan2(stage.cx - pivot.x, stage.cy - pivot.y);
  const swing = (t: number) =>
    hang +
    0.35 * Math.sin(3.2 * (t - enter.from)) * Math.exp(-0.9 * (t - enter.from));
  const rotation = (t: number) =>
    t < level.from
      ? swing(t)
      : lerp(swing(level.from), 0, easeInOut(seg(t, level.from, level.to)));
  const jolt = (t: number) =>
    NAIL_AT.reduce(
      (sum, at) =>
        t > at
          ? sum + 3 * Math.sin(50 * (t - at)) * Math.exp(-16 * (t - at))
          : sum,
      0,
    );

  return {
    settledAt: 3.6,
    place(t) {
      if (t < enter.from) return HIDDEN;
      const r = rotation(t);
      const dx = stage.cx - pivot.x;
      const dy = stage.cy - pivot.y;
      const drop =
        (1 - easeOut(seg(t, enter.from, enter.to))) *
        -(stage.cy + stage.cardHeight);
      return {
        x: pivot.x + dx * Math.cos(r) - dy * Math.sin(r),
        y: pivot.y + dx * Math.sin(r) + dy * Math.cos(r) + drop + jolt(t),
        rotation: r,
      };
    },
    draw(ctx, t) {
      // The nails leave a little before the other props (approved timing).
      const fade = 1 - seg(t, 5.4, 6.4);
      nails.forEach((nail, i) => {
        const at = i === 0 ? enter.to : NAIL_AT[i - 1]!;
        if (t < at) return;
        ctx.globalAlpha = fade;
        ctx.fillStyle = STEEL;
        circle(ctx, nail.x, nail.y, 5);
        ctx.fill();
        ctx.fillStyle = STEEL_LIGHT;
        circle(ctx, nail.x - 1.5, nail.y - 1.5, 2);
        ctx.fill();
        const k = seg(t, at, at + 0.3);
        if (i === 0 || k <= 0 || k >= 1) return;
        ctx.globalAlpha = 1 - k;
        ctx.strokeStyle = YELLOW;
        ctx.lineWidth = 2;
        ctx.beginPath();
        for (let a = 0; a < 8; a += 1) {
          const angle = (a * TAU) / 8;
          ctx.moveTo(
            nail.x + Math.cos(angle) * 8,
            nail.y + Math.sin(angle) * 8,
          );
          ctx.lineTo(
            nail.x + Math.cos(angle) * (10 + 18 * k),
            nail.y + Math.sin(angle) * (10 + 18 * k),
          );
        }
        ctx.stroke();
      });
      // The hammer works each nail in turn: wind up, strike, move on.
      const i = NAIL_AT.findIndex((at) => t < at + 0.2);
      if (i >= 0 && t > 1.7) {
        const at = NAIL_AT[i]!;
        const nail = nails[i + 1]!;
        const side = nail.x > stage.cx ? 1 : -1;
        const lift =
          t < at - 0.12
            ? easeOut(seg(t, at - 0.45, at - 0.12))
            : 1 - easeIn(seg(t, at - 0.12, at));
        const grip = { x: nail.x + side * 120, y: nail.y + 40 };
        const reach = Math.atan2(nail.y - 6 - grip.y, nail.x - grip.x);
        const length = Math.hypot(nail.x - grip.x, nail.y - 6 - grip.y);
        ctx.globalAlpha = seg(t, 1.7, 2.0) * (1 - seg(t, 3.5, 3.8));
        ctx.save();
        ctx.translate(grip.x, grip.y);
        ctx.rotate(reach + (t < at ? 1.1 * lift * side : 0));
        ctx.fillStyle = '#A16207';
        roundRect(ctx, -10, -5, length, 10, 4);
        ctx.fill();
        ctx.fillStyle = STEEL;
        roundRect(ctx, length - 12, -22, 22, 44, 4);
        ctx.fill();
        ctx.fillStyle = STEEL_LIGHT;
        ctx.fillRect(length - 12, -22, 22, 6);
        ctx.restore();
      }
      ctx.globalAlpha = 1;
    },
  };
};

// ── 20. Topping out ────────────────────────────────────────────────────────
const toppingOut: Builder = (stage, rng) => {
  const ground = stage.height;
  const grow = { from: 0.2, to: 3.2 };
  const towerWidth = stage.cardWidth * 0.82;
  // The card rides the central tower's roof up to its resting place.
  const roof = (t: number) =>
    t < 4.6
      ? lerp(
          ground + stage.cardHeight + 20,
          stage.bottom,
          easeInOut(seg(t, grow.from, grow.to)),
        )
      : lerp(stage.bottom, ground + 10, easeIn(seg(t, 4.6, 6.2)));
  const count = Math.max(4, Math.round(stage.width / 180));
  const skyline = Array.from({ length: count }, (_, i) => ({
    x: lerp(20, stage.width - 120, i / (count - 1)) + rand(rng, -20, 20),
    width: rand(rng, 60, 110),
    height: rand(rng, 0.25, 0.6) * stage.height,
    at: rand(rng, 0.1, 1.2),
    color: pick(rng, ['#64748B', '#475569', '#94A3B8', '#334155']),
  })).filter(
    (tower) =>
      tower.x + tower.width < stage.cx - towerWidth / 2 - 10 ||
      tower.x > stage.cx + towerWidth / 2 + 10,
  );
  const lit = Array.from({ length: 400 }, () => rng() < 0.55);
  const windows = (
    ctx: CanvasRenderingContext2D,
    x: number,
    top: number,
    width: number,
    offset: number,
  ) => {
    // Two fills (lit and dark panes), not one per window.
    for (const on of [true, false]) {
      ctx.fillStyle = on
        ? stage.dark
          ? '#FDE68A'
          : '#E0F2FE'
        : stage.dark
          ? '#1E293B'
          : '#CBD5E1';
      ctx.beginPath();
      let n = offset;
      for (let y = ground - 24; y > top + 10; y -= 22) {
        for (let wx = x + 10; wx < x + width - 18; wx += 18) {
          n = (n + 1) % lit.length;
          if (lit[n] === on) ctx.rect(wx, y, 9, 12);
        }
      }
      ctx.fill();
    }
  };
  const flag = { from: 3.1, to: 3.5 };
  const settledAt = grow.to + 0.05;

  return {
    settledAt,
    place: (t) => ({ y: roof(t) - stage.cardHeight / 2 }),
    draw(ctx, t) {
      const fade = propsAlpha(t);
      ctx.globalAlpha = fade;
      skyline.forEach((tower, i) => {
        const k =
          t < 4.6
            ? easeOut(seg(t, tower.at, tower.at + 1.6))
            : 1 - easeIn(seg(t, 4.8, 6.2));
        if (k <= 0) return;
        const top = ground - tower.height * k;
        ctx.fillStyle = tower.color;
        ctx.fillRect(tower.x, top, tower.width, ground - top);
        windows(ctx, tower.x, top, tower.width, i * 37);
      });
      const top = roof(t);
      const left = stage.cx - towerWidth / 2;
      ctx.fillStyle = stage.dark ? '#475569' : '#64748B';
      ctx.fillRect(left, top, towerWidth, ground - top);
      ctx.fillStyle = STEEL;
      ctx.fillRect(left - 8, top, towerWidth + 16, 8);
      windows(ctx, left, top + 6, towerWidth, 211);
      // The topping-out flag on the card's top corner.
      const up = seg(t, flag.from, flag.to) * (1 - seg(t, 5.4, 6.0));
      if (up > 0) {
        const x = stage.right - 24;
        const pole = 60 * easeBack(seg(t, flag.from, flag.to));
        const wave = Math.sin(t * 8) * 4;
        const y = stage.top - pole;
        ctx.globalAlpha = fade * up;
        ctx.strokeStyle = STEEL;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(x, stage.top);
        ctx.lineTo(x, y);
        ctx.stroke();
        ctx.fillStyle = '#16A34A';
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.quadraticCurveTo(x + 20, y - 6 + wave, x + 40, y + 2);
        ctx.lineTo(x + 40, y + 24);
        ctx.quadraticCurveTo(x + 20, y + 18 + wave, x, y + 22);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    },
  };
};

export const SECOND_TEN = {
  scaffolding,
  'pile-driver': pileDriver,
  'road-roller': roadRoller,
  'stop-slow': stopSlow,
  surveyor,
  'tunnel-borer': tunnelBorer,
  demolition,
  'tape-measure': tapeMeasure,
  'hammer-nails': hammerNails,
  'topping-out': toppingOut,
} satisfies Record<string, Builder>;
